import { getCookie } from '../services/CookieStorageService'
import type { Auth0ClientOptions } from '@auth0/auth0-spa-js'

/**
 * Extended Auth0 options that includes our custom properties
 * for cookies, blocked markets, and viewer country/region
 *
 * Note: We use 'unknown' for the index signature because Auth0ClientOptions
 * contains properties with various types (boolean, object, function) that would
 * conflict with a more restrictive type like 'string | number | undefined'
 */
export type ExtendedAuth0ClientOptions = Auth0ClientOptions & {
	[key: string]: unknown
}

/**
 * List of cookies that should be preserved and passed to Auth0
 * These cookies are used for tracking, analytics, and marketing attribution
 */
export const AUTH0_COOKIE_LIST: string[] = [
	'_fbp',
	'_fbc',
	'_gid',
	'_ga',
	'ECID',
	'AMCV(.*)AdobeOrg', // more than one
	'demdex',
	'aam_uuid',
	'adcloud',
	'OptanonConsent',
	'utm_source',
	'utm_medium',
	'utm_campaign',
	'utm_term',
	'utm_content',
	'document_url',
	'privacy-versioning-enabled',
]

function getCookies(): { name: string; value: string }[] {
	if (document.cookie === '') {
		return []
	}

	return document.cookie.split('; ').map(cookieString => {
		const cookie = cookieString.split(/=(.+)/)
		return {
			name: cookie[0]?.trim() || '',
			value: cookie[1] || ''
		}
	}).filter(cookie => cookie.name) // Filter out empty names
}

export function applyAuth0Cookies(options: ExtendedAuth0ClientOptions): void {
	getCookies()
		.filter(cookie => AUTH0_COOKIE_LIST.some(name => new RegExp(name + '$').test(cookie.name)))
		.forEach(cookie => {
			options[cookie.name] = cookie.value
		})
}

export function applyViewerCountryRegion(options: ExtendedAuth0ClientOptions): void {
	if (typeof viewerCountry !== 'undefined' && typeof viewerCountryRegion !== 'undefined') {
		options['viewer_country'] = getCookie('OVERRIDE_VIEWER_COUNTRY')?.value || viewerCountry
		options['viewer_country_region'] =
			getCookie('OVERRIDE_VIEWER_COUNTRY_REGION')?.value || viewerCountryRegion
	}
}

export function buildAuth0BaseOptions(config: {
	domain: string
	clientId: string
	audience: string
	platform: string
	redirectUri?: string
}): ExtendedAuth0ClientOptions {
	return {
		domain: config.domain,
		client_id: config.clientId,
		scope: 'openid profile email',
		audience: config.audience,
		redirect_uri: config.redirectUri || window.location.origin,
		platform: config.platform,
		leeway: 299,
	}
}
